<script setup lang="ts">
import {
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Pin as PinIcon,
  PinOff as PinOffIcon,
  X as XIcon,
} from '@lucide/vue'
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import InlineEditField from '@/components/ui/InlineEditField.vue'
import MobileActionSheet from '@/components/ui/MobileActionSheet.vue'
import { useEventListener } from '@/composables/useEventListener'
import { useSwipeAction } from '@/composables/useSwipeAction'
import { openAddToCollection, poiToAddToCollectionPoint } from '@/services/addToCollectionService'
import { showConfirm } from '@/services/dialogService'
import { downloadFile } from '@/services/downloadService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { MOBILE_PANEL_DRAG_START_EVENT } from '@/services/mobilePanelGestureService'
import { openMobileRenameSheet } from '@/services/mobileRenameService'
import { hasPermission, requirePermission } from '@/services/permissionService'
import { togglePOIPinned } from '@/services/pinnedService'
import { getPOIIcon } from '@/services/poiService'
import { showError } from '@/services/toastService'
import { isSmallScreen } from '@/services/uiService'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { WorkspacePOI } from '@/types/poi'
import { getCreatorByline } from '@/utils/creatorByline'
import { requireItemEditable } from '@/utils/permissionDialog'

import WorkspacePOIActionSheetContent from './WorkspacePOIActionSheetContent.vue'

const props = defineProps<{
  selectedPoiId?: string | null
  selectionMode?: boolean
  selectedPoiIds?: Set<string>
}>()

const emit = defineEmits<{
  'open-poi-detail': [poiId: string]
  'zoom-poi': [poiId: string]
  'toggle-selection': [poiId: string]
}>()

const workspaceStore = useWorkspaceStore()
const route = useRoute()
const uiStore = useUiStore()
const authStore = useAuthStore()
const renamingPOIId = ref<string | null>(null)
const renameValue = ref('')
const renameInputRef = ref<{ inputEl: HTMLInputElement | null } | null>(null)
const contextMenuPOIId = ref<string | null>(null)
const contextMenuStyle = ref<Record<string, string>>({})
const mobileContextMenuOpen = ref(false)
const longPressPOIId = ref<string | null>(null)
const longPressActivated = ref(false)
const suppressNextPOIRowClick = ref(false)
let longPressTimer: ReturnType<typeof setTimeout> | null = null
let longPressStartX = 0
let longPressStartY = 0

const POI_LONG_PRESS_MOVE_TOLERANCE_PX = 14

const pois = computed(() => workspaceStore.allWorkspacePOIs)
const canEditPOI = computed(() => hasPermission('edit_waypoint'))
const canDeletePOI = computed(() => hasPermission('delete_waypoint'))
const isPinnedWorkspace = computed(
  () =>
    route.name === 'pinned' ||
    (workspaceStore.isSystem === true && workspaceStore.systemKey === 'pinned'),
)

const updateFloatingPanelPosition = (
  event: MouseEvent,
  width: number,
  stylesRef: typeof contextMenuStyle,
) => {
  const viewportPadding = 8
  const left = Math.min(event.clientX, window.innerWidth - width - viewportPadding)
  const top = Math.min(event.clientY, window.innerHeight - 16 - viewportPadding)
  stylesRef.value = {
    position: 'fixed',
    left: `${Math.max(viewportPadding, left)}px`,
    top: `${Math.max(viewportPadding, top)}px`,
  }
}

const startInlineRename = async (poi: WorkspacePOI) => {
  closeContextMenu()
  // Creator-only enforcement (with an explanatory dialog) happens in
  // workspaceStore.updateWorkspacePOI on submit, so the affordance stays visible here.
  if (!(await requirePermission('edit_waypoint'))) return

  if (isSmallScreen.value) {
    const poiId = poi.clientId
    openMobileRenameSheet({
      title: 'Rename POI',
      currentName: poi.name || 'POI',
      submit: async (name) => {
        await workspaceStore.updateWorkspacePOI(poiId, { name })
      },
    })
    return
  }

  renameValue.value = poi.name || ''
  renamingPOIId.value = poi.clientId
  nextTick(() => {
    const input = renameInputRef.value?.inputEl
    if (input instanceof HTMLInputElement) {
      input.focus()
      input.select()
    }
  })
}

const commitInlineRename = async () => {
  if (!renamingPOIId.value) return
  const poiId = renamingPOIId.value
  const trimmed = renameValue.value.trim()
  renamingPOIId.value = null
  if (!trimmed) return
  await workspaceStore.updateWorkspacePOI(poiId, { name: trimmed })
}

const cancelInlineRename = () => {
  renamingPOIId.value = null
  renameValue.value = ''
}

const escapeXml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')

const buildPoiGpx = (poi: WorkspacePOI) => {
  const parts = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="RouteCamp" xmlns="http://www.topografix.com/GPX/1/1">',
    `  <wpt lat="${poi.lat}" lon="${poi.lon}">`,
  ]

  if (typeof poi.ele === 'number') {
    parts.push(`    <ele>${poi.ele}</ele>`)
  }
  if (poi.name?.trim()) {
    parts.push(`    <name>${escapeXml(poi.name.trim())}</name>`)
  }
  if (poi.description?.trim()) {
    parts.push(`    <desc>${escapeXml(poi.description.trim())}</desc>`)
  }
  if (poi.time) {
    parts.push(`    <time>${new Date(poi.time).toISOString()}</time>`)
  }
  if (poi.type?.trim()) {
    parts.push(`    <sym>${escapeXml(poi.type.trim())}</sym>`)
  }

  parts.push('  </wpt>', '</gpx>')
  return parts.join('\n')
}

const downloadPOI = async (poiId: string) => {
  const poi = workspaceStore.pois.find((candidate) => candidate.clientId === poiId)
  if (!poi) return
  closeContextMenu()

  const gpx = buildPoiGpx(poi)
  const blob = new Blob([gpx], { type: 'application/gpx+xml' })
  const fileName = `${(poi.name?.trim() || 'poi').replace(/[\\/:*?"<>|]+/g, '_')}.gpx`

  try {
    await downloadFile(blob, fileName, { mimeType: 'application/gpx+xml' })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download POI GPX', { error })
    showError(`Failed to download POI: ${error instanceof Error ? error.message : String(error)}`)
  }
}

const deletePOI = async (poiId: string) => {
  const poi = workspaceStore.pois.find((candidate) => candidate.clientId === poiId)
  if (!poi) return
  closeContextMenu()
  if (!(await requirePermission('delete_waypoint'))) return
  // Check creator-only content edit *before* the "are you sure" prompt, so a non-creator sees
  // only the explainer instead of a confirm they can't actually act on.
  if (!(await requireItemEditable(poi.canEdit, 'waypoint', poi.createdByName))) return

  const confirmed = await showConfirm({
    title: 'Delete POI',
    message: `Are you sure you want to delete "${poi.name || 'POI'}"? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })
  if (!confirmed) return

  await workspaceStore.deleteWorkspacePOI(poiId)
  if (renamingPOIId.value === poiId) {
    cancelInlineRename()
  }
}

const handleTogglePOIPinned = async (poi: WorkspacePOI) => {
  await togglePOIPinned(poi)
}

const clearLongPressTimer = () => {
  if (longPressTimer !== null) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

const cancelPOIRowLongPress = () => {
  clearLongPressTimer()
  longPressPOIId.value = null
}

const poiSwipe = useSwipeAction<WorkspacePOI>({
  getId: (poi) => poi.clientId,
  isDisabled: () => renamingPOIId.value !== null,
  onTrigger: (poi) => handleTogglePOIPinned(poi),
})

const getPOISwipeLabel = (poi: WorkspacePOI) => (poi.isPinned ? 'Unpin' : 'Pin')

const clearContextMenuPOIHighlight = () => {
  const poiId = contextMenuPOIId.value
  if (!poiId || props.selectedPoiId === poiId) return
  if (uiStore.hoveredWaypointId === `poi_${poiId}`) {
    setHoveredPOI(null)
  }
}

const closeContextMenu = () => {
  if (mobileContextMenuOpen.value) {
    closeMobileContextMenu()
    return
  }
  clearContextMenuPOIHighlight()
  contextMenuPOIId.value = null
}

const closeMobileContextMenu = () => {
  if (!mobileContextMenuOpen.value) return
  mobileContextMenuOpen.value = false
}

const cancelPOIContextMenuForPanelDrag = () => {
  cancelPOIRowLongPress()
  longPressActivated.value = false
  suppressNextPOIRowClick.value = false
  if (mobileContextMenuOpen.value) closeMobileContextMenu()
}

const handleMobileContextMenuClosed = () => {
  clearContextMenuPOIHighlight()
  contextMenuPOIId.value = null
  longPressPOIId.value = null
  longPressActivated.value = false
  suppressNextPOIRowClick.value = false
}

const openPOIContextMenu = (poiId: string, event: MouseEvent) => {
  event.preventDefault()
  event.stopPropagation()
  if (isCoarsePointer()) return
  contextMenuPOIId.value = poiId
  setHoveredPOI(poiId)
  updateFloatingPanelPosition(event, 192, contextMenuStyle)
}

const contextMenuPOI = computed(() =>
  contextMenuPOIId.value
    ? (workspaceStore.pois.find((candidate) => candidate.clientId === contextMenuPOIId.value) ??
      null)
    : null,
)

const isCoarsePointer = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches

const startPOILongPress = (event: TouchEvent, poi: WorkspacePOI) => {
  if (renamingPOIId.value === poi.clientId) return
  if (event.touches.length !== 1) return
  const target = event.target instanceof HTMLElement ? event.target : null
  const interactiveTarget = target?.closest('button,input,textarea,[contenteditable="true"]')
  if (interactiveTarget && !interactiveTarget.closest('[data-poi-row-primary]')) return

  clearLongPressTimer()
  longPressActivated.value = false
  longPressPOIId.value = poi.clientId
  longPressStartX = event.touches[0].clientX
  longPressStartY = event.touches[0].clientY

  longPressTimer = setTimeout(() => {
    longPressTimer = null
    longPressActivated.value = true
    suppressNextPOIRowClick.value = true
    longPressPOIId.value = poi.clientId
    poiSwipe.cancel(poi)
    try {
      navigator.vibrate?.(30)
    } catch (error) {
      logService.debug(LOG_CATEGORIES.UI, 'Long-press haptic feedback unavailable', { error })
    }
    emit('zoom-poi', poi.clientId)
    contextMenuPOIId.value = poi.clientId
    setHoveredPOI(poi.clientId)
    mobileContextMenuOpen.value = true
  }, 500)
}

const endPOILongPress = () => {
  clearLongPressTimer()
  longPressPOIId.value = null
}

const onPOIRowTouchStart = (event: TouchEvent, poi: WorkspacePOI) => {
  poiSwipe.start(event, poi)
  startPOILongPress(event, poi)
}

const onPOIRowTouchMove = (event: TouchEvent, poi: WorkspacePOI) => {
  if (longPressTimer !== null && event.touches.length === 1) {
    const touch = event.touches[0]
    const dx = touch.clientX - longPressStartX
    const dy = touch.clientY - longPressStartY
    const distance = Math.hypot(dx, dy)
    if (distance > POI_LONG_PRESS_MOVE_TOLERANCE_PX) {
      cancelPOIRowLongPress()
    }
  }
  poiSwipe.move(event, poi)
}

const onPOIRowTouchEnd = (event: TouchEvent, poi: WorkspacePOI) => {
  if (longPressActivated.value || contextMenuPOIId.value === poi.clientId) {
    event.preventDefault()
    event.stopPropagation()
    poiSwipe.cancel(poi)
    endPOILongPress()
    return
  }
  poiSwipe.end(poi)
  endPOILongPress()
}

const onPOIRowTouchCancel = (poi: WorkspacePOI) => {
  poiSwipe.cancel(poi)
  endPOILongPress()
}

const handleContextMenuRename = async () => {
  if (!contextMenuPOIId.value) return
  const poi = workspaceStore.pois.find((candidate) => candidate.clientId === contextMenuPOIId.value)
  if (!poi) return
  await startInlineRename(poi)
}

const handleContextMenuDownload = async () => {
  if (!contextMenuPOIId.value) return
  await downloadPOI(contextMenuPOIId.value)
}

const handleContextMenuCopy = (event?: MouseEvent) => {
  if (!contextMenuPOIId.value) return
  openAddToCollectionPicker(contextMenuPOIId.value, event)
}

const handleContextMenuToggleVisibility = () => {
  if (!contextMenuPOIId.value) return
  const poiId = contextMenuPOIId.value
  closeContextMenu()
  uiStore.toggleSinglePOIVisibility(poiId)
}

const handleContextMenuPin = async () => {
  if (!contextMenuPOIId.value) return
  const poi = workspaceStore.pois.find((candidate) => candidate.clientId === contextMenuPOIId.value)
  if (!poi) return
  closeContextMenu()
  await handleTogglePOIPinned(poi)
}

const handleContextMenuDelete = async () => {
  if (!contextMenuPOIId.value) return
  await deletePOI(contextMenuPOIId.value)
}

const openAddToCollectionPicker = (poiId: string, event?: MouseEvent) => {
  if (!authStore.isAuthenticated) {
    window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'login' } }))
    return
  }
  event?.stopPropagation()
  const poi = workspaceStore.pois.find((candidate) => candidate.clientId === poiId)
  if (!poi) return
  closeContextMenu()
  openAddToCollection({
    points: [poiToAddToCollectionPoint(poi)],
    sourceWorkspaceId: poi.workspaceClientId,
  })
}

const setHoveredPOI = (poiId: string | null) => {
  uiStore.setHoveredWaypoint(poiId ? `poi_${poiId}` : null)
}

const isSelectedPOI = (poiId: string) => props.selectedPoiId === poiId

const isHoveredPOI = (poiId: string) => uiStore.hoveredWaypointId === `poi_${poiId}`

const isPOIVisible = (poiId: string) => !uiStore.isPOIHidden(poiId)
// Effective on-map visibility: a POI is hidden if it is individually hidden OR the whole POI
// section is toggled off. List rows reflect this (grayed + eye-off) so the hidden state is shown.
const isPOIEffectivelyVisible = (poiId: string) =>
  uiStore.arePOIsVisible && !uiStore.isPOIHidden(poiId)

const toggleSinglePOIVisibility = (poiId: string, event: Event) => {
  event.stopPropagation()
  uiStore.toggleSinglePOIVisibility(poiId)
}

const handlePOIRowClick = (poiId: string) => {
  if (poiSwipe.consumeSuppressedClick()) return
  if (suppressNextPOIRowClick.value) {
    suppressNextPOIRowClick.value = false
    return
  }
  if (longPressActivated.value) {
    longPressActivated.value = false
    return
  }
  setHoveredPOI(poiId)
  emit('zoom-poi', poiId)
  emit('open-poi-detail', poiId)
}

const onPOIRowClickCapture = (event: MouseEvent) => {
  if (!suppressNextPOIRowClick.value && !mobileContextMenuOpen.value) return
  event.preventDefault()
  event.stopPropagation()
  suppressNextPOIRowClick.value = false
}

const handlePOIMouseLeave = (poiId: string) => {
  if (isSelectedPOI(poiId)) {
    setHoveredPOI(poiId)
    return
  }
  setHoveredPOI(null)
}

watch(
  () => props.selectedPoiId ?? null,
  (selectedPoiId, previousSelectedPoiId) => {
    if (selectedPoiId) {
      setHoveredPOI(selectedPoiId)
      return
    }

    const previousHoverId = previousSelectedPoiId ? `poi_${previousSelectedPoiId}` : null
    if (previousHoverId && uiStore.hoveredWaypointId === previousHoverId) {
      uiStore.setHoveredWaypoint(null)
    }
  },
  { immediate: true },
)

useEventListener(document, 'pointerdown', (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('[data-poi-context-menu]') && !target.closest('.mobile-action-sheet-panel')) {
    closeContextMenu()
  }
})
useEventListener(window, MOBILE_PANEL_DRAG_START_EVENT, cancelPOIContextMenuForPanelDrag)
</script>

<template>
  <TransitionGroup
    v-if="pois.length > 0"
    tag="ul"
    class="space-y-1 px-4 pb-3"
    :name="isPinnedWorkspace ? 'pinned-unpin-slide' : undefined"
    data-testid="workspace-poi-list"
  >
    <li
      v-for="poi in pois"
      :key="poi.clientId"
      class="group/row relative -mx-1 overflow-hidden rounded transition-colors duration-200 hover:cursor-pointer"
      @mouseenter="setHoveredPOI(poi.clientId)"
      @mouseleave="handlePOIMouseLeave(poi.clientId)"
      @contextmenu="openPOIContextMenu(poi.clientId, $event)"
    >
      <div
        class="absolute inset-0 flex items-center justify-between border-y border-amber-200/70 bg-amber-500 px-5 text-white"
        aria-hidden="true"
      >
        <div class="flex items-center gap-3">
          <component :is="poi.isPinned ? PinOffIcon : PinIcon" class="h-5 w-5 shrink-0" />
          <span class="text-xs font-semibold leading-tight">{{ getPOISwipeLabel(poi) }}</span>
        </div>
        <div class="flex items-center gap-3">
          <span class="text-xs font-semibold leading-tight">{{ getPOISwipeLabel(poi) }}</span>
          <component :is="poi.isPinned ? PinOffIcon : PinIcon" class="h-5 w-5 shrink-0" />
        </div>
      </div>

      <div
        class="relative flex items-start rounded px-1 py-1.5 transition-colors duration-200"
        :class="
          isSelectedPOI(poi.clientId) || isHoveredPOI(poi.clientId)
            ? 'bg-gray-200'
            : 'bg-white hover:bg-gray-200'
        "
        :style="{
          transform: `translateX(${poiSwipe.translateX[poi.clientId] ?? 0}px)`,
          transition: poiSwipe.transitioning[poi.clientId] ? 'transform 0.25s ease' : undefined,
        }"
        @touchstart.passive="onPOIRowTouchStart($event, poi)"
        @touchmove="onPOIRowTouchMove($event, poi)"
        @touchend="onPOIRowTouchEnd($event, poi)"
        @touchcancel="onPOIRowTouchCancel(poi)"
        @click.capture="onPOIRowClickCapture"
        @transitionend="poiSwipe.clearTransition(poi)"
      >
        <BaseCheckbox
          v-if="props.selectionMode"
          class="mr-2 mt-0.5 shrink-0"
          :model-value="props.selectedPoiIds?.has(poi.clientId) ?? false"
          @update:model-value="emit('toggle-selection', poi.clientId)"
          @click.stop
        />
        <div class="mr-2 shrink-0">
          <span
            class="inline-flex h-6 w-6 items-center justify-center rounded-full border border-white bg-orange-500 text-white shadow-sm transition-opacity"
            :class="{ 'opacity-40 grayscale': !isPOIEffectivelyVisible(poi.clientId) }"
          >
            <component :is="getPOIIcon(poi.type)" class="h-3.5 w-3.5 scale-90" />
          </span>
        </div>
        <div class="flex min-w-0 flex-1 items-center">
          <button
            v-if="renamingPOIId !== poi.clientId"
            data-poi-row-primary
            type="button"
            class="flex min-w-0 flex-1 items-start text-left hover:cursor-pointer"
            @click="
              props.selectionMode
                ? emit('toggle-selection', poi.clientId)
                : handlePOIRowClick(poi.clientId)
            "
          >
            <span class="min-w-0">
              <span class="flex min-w-0 items-baseline gap-1">
                <span
                  class="min-w-0 truncate text-sm text-gray-700"
                  :class="{ '!text-gray-400': !isPOIEffectivelyVisible(poi.clientId) }"
                >
                  {{ poi.name || 'POI' }}
                </span>
                <span
                  v-if="getCreatorByline(poi)"
                  class="shrink-0 text-[11px] text-gray-500"
                  data-testid="poi-creator-byline"
                >
                  {{ getCreatorByline(poi) }}
                </span>
              </span>
              <span v-if="poi.description" class="block truncate text-[11px] text-gray-500">
                {{ poi.description }}
              </span>
            </span>
          </button>
          <InlineEditField
            v-else
            ref="renameInputRef"
            v-model="renameValue"
            @commit="commitInlineRename"
            @cancel="cancelInlineRename"
          />
          <button
            v-if="renamingPOIId !== poi.clientId"
            type="button"
            class="ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-500 transition-colors hover:cursor-pointer hover:text-amber-600"
            :class="poi.isPinned ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100'"
            :title="getPOISwipeLabel(poi)"
            :aria-label="getPOISwipeLabel(poi)"
            @click.stop="handleTogglePOIPinned(poi)"
          >
            <PinIcon class="h-3.5 w-3.5" :class="{ 'fill-current text-amber-600': poi.isPinned }" />
          </button>
          <button
            v-if="renamingPOIId !== poi.clientId"
            type="button"
            class="ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors hover:cursor-pointer"
            :class="
              isPOIVisible(poi.clientId)
                ? 'text-gray-500 hover:text-gray-700 opacity-0 group-hover/row:opacity-100'
                : 'text-gray-500 hover:text-gray-700 opacity-100'
            "
            :title="isPOIVisible(poi.clientId) ? 'Hide on map' : 'Show on map'"
            :aria-label="isPOIVisible(poi.clientId) ? 'Hide on map' : 'Show on map'"
            @click="toggleSinglePOIVisibility(poi.clientId, $event)"
          >
            <component
              :is="isPOIVisible(poi.clientId) ? EyeIcon : EyeOffIcon"
              class="h-3.5 w-3.5"
            />
          </button>
        </div>
      </div>
    </li>
  </TransitionGroup>

  <Teleport to="body">
    <div
      v-if="contextMenuPOI && !isSmallScreen"
      data-poi-context-menu
      :style="contextMenuStyle"
      class="fixed z-[9999] w-48 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
    >
      <WorkspacePOIActionSheetContent
        :is-pinned-workspace="isPinnedWorkspace"
        :is-pinned="contextMenuPOI.isPinned === true"
        :is-visible="isPOIVisible(contextMenuPOI.clientId)"
        :can-edit="canEditPOI"
        :can-delete="canDeletePOI"
        density="compact"
        @toggle-pinned="handleContextMenuPin"
        @toggle-visibility="handleContextMenuToggleVisibility"
        @download="handleContextMenuDownload"
        @add-to-collection="handleContextMenuCopy"
        @rename="handleContextMenuRename"
        @delete="handleContextMenuDelete"
      />
    </div>
  </Teleport>

  <MobileActionSheet
    v-if="contextMenuPOI"
    :open="mobileContextMenuOpen"
    :title="contextMenuPOI.name || 'POI'"
    :title-byline="getCreatorByline(contextMenuPOI)"
    dot-color="#f97316"
    @close="closeContextMenu"
    @closed="handleMobileContextMenuClosed"
  >
    <template #header-left>
      <button
        type="button"
        class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
        title="Close"
        aria-label="Close POI actions"
        @click="closeContextMenu"
      >
        <XIcon class="h-4 w-4" />
      </button>
    </template>

    <WorkspacePOIActionSheetContent
      :is-pinned-workspace="isPinnedWorkspace"
      :is-pinned="contextMenuPOI.isPinned === true"
      :is-visible="isPOIVisible(contextMenuPOI.clientId)"
      :can-edit="canEditPOI"
      :can-delete="canDeletePOI"
      @toggle-pinned="handleContextMenuPin"
      @toggle-visibility="handleContextMenuToggleVisibility"
      @download="handleContextMenuDownload"
      @add-to-collection="handleContextMenuCopy"
      @rename="handleContextMenuRename"
      @delete="handleContextMenuDelete"
    />
  </MobileActionSheet>
</template>

<style scoped>
.pinned-unpin-slide-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
}

.pinned-unpin-slide-move {
  transition: transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
}

.pinned-unpin-slide-leave-to {
  opacity: 0;
  transform: translateY(-14px);
}
</style>
